import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import useAuthStore from '../store/authStore';

const SupplierRegister = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [activeSection, setActiveSection] = useState(1);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);

  const [formData, setFormData] = useState({
    // 1. Business Model & Type
    businessType: '', // B2B, B2C, Both
    businessNature: '', // Manufacturer, Retailer, etc.
    businessNatureOther: '',

    // 2. Company Details
    businessName: '',
    brandName: '',
    yearOfEstablishment: '',
    legalEntityType: '', // Proprietorship, etc.

    // 3. Location Details
    headOfficeCountry: '',
    state: '', // Territory / State / Province
    city: '', // City / District
    areaTown: '',
    businessAddress: '',
    pincode: '',
    googleMapLocation: '',

    // 4. Contact & Communication
    ownerName: '',
    designation: '',
    phone: '',
    whatsappNumber: '',
    email: '',
    companyWebsiteUrl: '',
    alternateContactNumber: '',
    password: '',

    // 5. Business Category
    primaryBusinessCategory: '',
    secondaryCategories: [], // Array of strings?

    // 6. Products / Services Information
    productsServicesOffered: '',
    keyProductService: '',
    industryServed: '',
    serviceCoverageArea: {
      Local: false,
      State: false,
      National: false,
      International: false
    },

    // 7. Legal Compliance
    gstRegistered: false,
    msmeRegistered: false,
    fssaiRegistered: false,
    tradeLicenseRegistered: false,
    iecRegistered: false,
    isoRegistered: false,
    professionalCertificateRegistered: false,
    otherApprovalRegistered: false,

    // 8. Document Uploads (Files)
    certificateFiles: [],
    trainedCertificateFiles: [],

    // 9. Media Uploads (Files)
    productPhotos: [],
    officePhotos: [],
    logo: null,

    // 10. About Your Business
    description: '', // Brief Profile
    uspStrengths: '',
    awardsAchievements: '',

    // 12. Declaration
    declarationAccepted: false,
    declarationDate: new Date().toISOString().split('T')[0],
    applicantSignature: '', // Typed Name
    referralCode: ''        // Mobile number of the person who referred this supplier
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === 'checkbox' && name.startsWith('coverage_')) {
      const area = name.split('_')[1];
      setFormData(prev => ({
        ...prev,
        serviceCoverageArea: {
          ...prev.serviceCoverageArea,
          [area]: checked
        }
      }));
    } else if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleFileChange = (e, fieldName, multiple = false) => {
    const files = Array.from(e.target.files);
    if (multiple) {
      setFormData(prev => ({ ...prev, [fieldName]: [...prev[fieldName], ...files] }));
    } else {
      setFormData(prev => ({ ...prev, [fieldName]: files[0] }));
    }
  };

  const removeFile = (fieldName, index) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: prev[fieldName].filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.declarationAccepted) {
      toast.error('Please accept the declaration to proceed.');
      return;
    }

    setLoading(true);

    try {
      const submitData = new FormData();

      // Append text fields
      Object.keys(formData).forEach(key => {
        if (['certificateFiles', 'trainedCertificateFiles', 'productPhotos', 'officePhotos', 'logo', 'serviceCoverageArea', 'secondaryCategories'].includes(key)) {
          return;
        }
        submitData.append(key, formData[key]);
      });

      // Append JSON/Complex fields
      submitData.append('serviceCoverageArea', JSON.stringify(formData.serviceCoverageArea));
      // Simple secondary categories (comma separated string for now or ensure backend handles array)
      if (formData.secondaryCategories) {
        submitData.append('secondaryCategories', JSON.stringify(formData.secondaryCategories));
      }

      // Append Files
      if (formData.logo) submitData.append('logo', formData.logo);
      formData.certificateFiles.forEach(file => submitData.append('certificateFiles', file));
      formData.trainedCertificateFiles.forEach(file => submitData.append('trainedCertificateFiles', file));
      formData.productPhotos.forEach(file => submitData.append('productPhotos', file));
      formData.officePhotos.forEach(file => submitData.append('officePhotos', file));

      // DEBUG LOGGING
      console.log('DEBUG: Submitting Form Data');
      for (let pair of submitData.entries()) {
        console.log(pair[0] + ': ' + (pair[1] instanceof File ? pair[1].name : pair[1]));
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/supplier/register`, {
        method: 'POST',
        body: submitData // Content-Type header handled automatically for FormData
      });

      const data = await response.json();

      if (response.ok) {
        setShowWelcomeModal(true);
      } else {
        toast.error(data.message || 'Registration failed');
        console.error('Registration error:', data);
      }
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const sectionTitles = {
    1: 'Business Model & Type',
    2: 'Company Details',
    3: 'Location Details',
    4: 'Contact & Communication',
    5: 'Business Category',
    6: 'Products / Services',
    7: 'Legal Compliance',
    8: 'Document Upload',
    9: 'Media Upload',
    10: 'About Business',
    11: 'Review',
    12: 'Declaration'
  };

  const nextSection = () => setActiveSection(prev => Math.min(prev + 1, 12));
  const prevSection = () => setActiveSection(prev => Math.max(prev - 1, 1));

  // ─── Tamil Welcome Modal ─────────────────────────────────────────────────
  const WelcomeModal = () => (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)' }}>
      <div className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl"
        style={{
          background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%)',
          border: '1px solid rgba(245,158,11,0.4)',
          boxShadow: '0 0 60px rgba(245,158,11,0.15)'
        }}>
        <div className="h-1 w-full rounded-t-2xl" style={{ background: 'linear-gradient(90deg, #f59e0b, #ea580c, #f59e0b)' }} />
        <div className="p-8">
          <div className="text-center mb-6">
            <div className="text-4xl mb-2">🌟</div>
            <h2 className="text-2xl font-bold" style={{ color: '#f59e0b' }}>GoodBuy Tamizha</h2>
            <p className="text-amber-400/70 text-sm mt-1">தமிழர் வணிகத்தின் உலக மேடை</p>
          </div>
          <div className="space-y-4 text-base leading-relaxed" style={{ color: '#e2e8f0' }}>
            <p className="text-amber-300 font-semibold text-lg">வணக்கம்</p>
            <p>குட் Buy தமிழாவில் <span className="text-amber-400 font-medium">www.goodbuytamizha.com</span> ல் தங்கள் வணிகத்தை இணைத்ததற்கு மிக்க நன்றி. மனமார்ந்த வாழ்த்துக்கள்.</p>
            <p><span className="text-amber-300 font-semibold">&ldquo;தமிழர் வணிகம் தரணி எங்கும்&rdquo;</span> என்ற உயர்ந்த நோக்கத்துடன் உருவாக்கப்பட்டுள்ள <span className="text-amber-400">www.goodbuytamizha.com</span> தரமான தமிழர் வணிகங்களை உலகளவில் இணைக்கும் ஒரு முன்னேற்றமான டிஜிட்டல் வர்த்தக தளமாக திகழ்கிறது.</p>
            <p>விரைவில் அறிமுகமாகும் <span className="text-amber-300 font-medium">GoodBuy Tamizha Android Application</span> மூலம், உங்கள் வணிகம் ஆன்லைனில் தேடி வாங்கும் ஆயிரக்கணக்கான வாடிக்கையாளர்களை நேரடியாக சென்றடையும். உங்கள் சேவைகள் மற்றும் தயாரிப்புகள் சரியான வாடிக்கையாளர்களை, சரியான இடத்தில், சரியான நேரத்தில் அடைய வடிவமைக்கப்பட்டுள்ளன.</p>
            <div className="rounded-xl p-4 space-y-1" style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)' }}>
              <p className="text-amber-300 font-medium">Google Location அடிப்படையிலான செயல்பாடு மூலம்,</p>
              <p>• வாடிக்கையாளர்கள் தங்களுக்கு அருகிலுள்ள தரமான தமிழர் வணிகங்களை எளிதில் கண்டறிய முடியும்.</p>
              <p>• வணிகர்களும் தங்களுக்கருகில் உள்ள வாடிக்கையாளர்களை திறம்பட அடைய முடியும்.</p>
            </div>
            <p>இது வசதியை மட்டுமல்ல — வணிக வளர்ச்சிக்கான ஒரு திறந்த வாயில். எங்கள் தளத்தில் பதிவு செய்யப்படும் நிறுவனங்கள்: உரிய சான்றிதழ்களுடன், தகுந்த லைசென்ஸுடன், தமிழர் உரிமையாளர் தலைமையில் இயங்கும் நிறுவனங்களாக இருக்க வேண்டும்.</p>
            <p className="text-amber-300">ஏனெனில் GoodBuy Tamizha தரத்தை முன்னிலைப்படுத்துகிறது; நம்பிக்கையை மதிக்கிறது; வளர்ச்சியை உருவாக்குகிறது.</p>
            <p>இதுவரை ஒரு பகுதியில் மட்டுமே அறியப்பட்டிருந்த தரமான தமிழர் வணிகங்களுக்கு, இனி உலகளாவிய காட்சி மற்றும் விரிவாக்க வாய்ப்பு உருவாகிறது.</p>
            <div className="rounded-xl p-4" style={{ background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.25)' }}>
              <p className="font-semibold text-indigo-300 mb-1">தரமான தமிழர் வணிக நண்பர்களையும் இணைக்கவும்.</p>
              <p>நாம் ஒன்றாக வளர்ந்தால் தான், தமிழர் வணிகம் உலகம் முழுவதும் உயர்ந்து நிற்கும்.</p>
            </div>
            <p>மேலும் பல புதுப்பிப்புகள் மற்றும் முன்னேற்றங்களுடன் விரைவில் உங்களை மீண்டும் சந்திக்கிறோம்.</p>
            <p className="text-center text-amber-400 font-bold text-lg pt-2">நன்றி — குட் Buy தமிழா 🙏</p>
          </div>
          <button
            onClick={() => { setShowWelcomeModal(false); navigate('/supplier/login'); }}
            className="mt-8 w-full py-4 rounded-xl font-bold text-lg text-white transition-all duration-300 hover:scale-105"
            style={{ background: 'linear-gradient(135deg, #f59e0b, #ea580c)', boxShadow: '0 8px 32px rgba(245,158,11,0.35)' }}>
            உள்நுழைக &nbsp;→&nbsp; Login
          </button>
        </div>
        <div className="h-1 w-full rounded-b-2xl" style={{ background: 'linear-gradient(90deg, #f59e0b, #ea580c, #f59e0b)' }} />
      </div>
    </div>
  );

  return (
    <>
      {showWelcomeModal && <WelcomeModal />}
      <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8">

        <div className="max-w-5xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            {/* Header */}
            <div className="bg-indigo-600 px-8 py-6 text-white">
              <h1 className="text-3xl font-bold">Global Tamil Business Registration</h1>
              <p className="mt-2 text-indigo-100">Join the world's largest Tamil business network</p>
            </div>

            {/* Progress Bar */}
            <div className="bg-gray-100 px-8 py-4 border-b">
              <div className="flex items-center justify-between text-sm font-medium text-gray-500 mb-2">
                <span>Step {activeSection} of 12</span>
                <span>{Math.round((activeSection / 12) * 100)}% Completed</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(activeSection / 12) * 100}%` }}
                />
              </div>
              <h2 className="mt-4 text-xl font-bold text-gray-900">{sectionTitles[activeSection]}</h2>
            </div>

            <form onSubmit={handleSubmit} className="px-8 py-8">

              {/* 1. Business Model & Type */}
              {activeSection === 1 && (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Business Type *</label>
                    <div className="flex gap-4">
                      {['B2B', 'B2C', 'Both'].map(type => (
                        <label key={type} className={`flex-1 border rounded-xl p-4 cursor-pointer hover:border-indigo-500 transition-colors ${formData.businessType === type ? 'border-indigo-600 bg-indigo-50' : 'border-gray-200'}`}>
                          <input
                            type="radio"
                            name="businessType"
                            value={type}
                            checked={formData.businessType === type}
                            onChange={handleInputChange}
                            className="sr-only"
                          />
                          <div className="text-center font-semibold text-gray-900">{type}</div>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Business Nature *</label>
                    <select
                      name="businessNature"
                      value={formData.businessNature}
                      onChange={handleInputChange}
                      className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-3 border"
                    >
                      <option value="">Select Business Nature</option>
                      <option value="Manufacturer">Manufacturer</option>
                      <option value="Retailer">Retailer</option>
                      <option value="Wholesaler">Wholesaler</option>
                      <option value="Distributor">Distributor</option>
                      <option value="Service Provider">Service Provider</option>
                      <option value="Exporter / Importer">Exporter / Importer</option>
                      <option value="Consultant / Freelancer">Consultant / Freelancer</option>
                      <option value="Real Estate">Real Estate</option>
                      <option value="Others">Others</option>
                    </select>
                    {formData.businessNature === 'Others' && (
                      <input
                        type="text"
                        name="businessNatureOther"
                        value={formData.businessNatureOther}
                        onChange={handleInputChange}
                        className="mt-2 w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-3 border"
                        placeholder="Please specify your business nature"
                        required
                      />
                    )}
                  </div>
                </div>
              )}

              {/* 2. Company Details */}
              {activeSection === 2 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Company / Business Name *</label>
                    <input type="text" name="businessName" value={formData.businessName} onChange={handleInputChange} className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-3 border" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Brand Name (if different)</label>
                    <input type="text" name="brandName" value={formData.brandName} onChange={handleInputChange} className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-3 border" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Year of Establishment</label>
                    <input type="number" name="yearOfEstablishment" value={formData.yearOfEstablishment} onChange={handleInputChange} className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-3 border" placeholder="YYYY" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Legal Entity Type</label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {['Proprietorship', 'Partnership', 'LLP', 'Private Limited', 'Public Limited', 'Others'].map(type => (
                        <label key={type} className={`border rounded-lg p-3 cursor-pointer hover:border-indigo-500 transition-colors ${formData.legalEntityType === type ? 'border-indigo-600 bg-indigo-50' : 'border-gray-200'}`}>
                          <input type="radio" name="legalEntityType" value={type} checked={formData.legalEntityType === type} onChange={handleInputChange} className="sr-only" />
                          <div className="text-center text-sm font-medium text-gray-900">{type}</div>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* 3. Location Details */}
              {activeSection === 3 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Country *</label>
                    <input type="text" name="headOfficeCountry" value={formData.headOfficeCountry} onChange={handleInputChange} className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-3 border" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Territory / State / Province *</label>
                    <input type="text" name="state" value={formData.state} onChange={handleInputChange} className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-3 border" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">City / District *</label>
                    <input type="text" name="city" value={formData.city} onChange={handleInputChange} className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-3 border" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Area / Town</label>
                    <input type="text" name="areaTown" value={formData.areaTown} onChange={handleInputChange} className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-3 border" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Full Address *</label>
                    <textarea name="businessAddress" value={formData.businessAddress} onChange={handleInputChange} className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-3 border" rows="3" required></textarea>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Pincode / Zip Code *</label>
                    <input type="text" name="pincode" value={formData.pincode} onChange={handleInputChange} className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-3 border" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Google Map Location Link (Optional)</label>
                    <input type="url" name="googleMapLocation" value={formData.googleMapLocation} onChange={handleInputChange} className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-3 border" placeholder="https://maps.google.com/..." />
                  </div>
                </div>
              )}

              {/* 4. Contact & Communication */}
              {activeSection === 4 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Contact Person Name *</label>
                    <input type="text" name="ownerName" value={formData.ownerName} onChange={handleInputChange} className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-3 border" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Designation *</label>
                    <input type="text" name="designation" value={formData.designation} onChange={handleInputChange} className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-3 border" placeholder="Owner / Director / Partner / Manager" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Contact Phone Number *</label>
                    <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-3 border" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">WhatsApp Number *</label>
                    <input type="tel" name="whatsappNumber" value={formData.whatsappNumber} onChange={handleInputChange} className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-3 border" required />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email ID *</label>
                    <input type="email" name="email" value={formData.email} onChange={handleInputChange} className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-3 border" required />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Password *</label>
                    <input type="password" name="password" value={formData.password} onChange={handleInputChange} className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-3 border" required placeholder="Choose a secure password" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Website URL (if available)</label>
                    <input type="url" name="companyWebsiteUrl" value={formData.companyWebsiteUrl} onChange={handleInputChange} className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-3 border" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Alternate Contact Number (Optional)</label>
                    <input type="tel" name="alternateContactNumber" value={formData.alternateContactNumber} onChange={handleInputChange} className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-3 border" />
                  </div>
                </div>
              )}

              {/* 5. Business Category */}
              {activeSection === 5 && (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Primary Business Category *</label>
                    <input type="text" name="primaryBusinessCategory" value={formData.primaryBusinessCategory} onChange={handleInputChange} className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-3 border" required placeholder="E.g., Textiles, IT Services, Food & Beverage" />
                  </div>
                  {/* Secondary Categories could be a tagging system, keeping simple for now */}
                </div>
              )}

              {/* 6. Products / Services Information */}
              {activeSection === 6 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Products / Services Offered *</label>
                    <textarea name="productsServicesOffered" value={formData.productsServicesOffered} onChange={handleInputChange} className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-3 border" rows="3" required></textarea>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Key Product / Core Service</label>
                    <input type="text" name="keyProductService" value={formData.keyProductService} onChange={handleInputChange} className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-3 border" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Industry Served</label>
                    <input type="text" name="industryServed" value={formData.industryServed} onChange={handleInputChange} className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-3 border" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-3">Service Coverage Area</label>
                    <div className="flex flex-wrap gap-4">
                      {['Local', 'State', 'National', 'International'].map(area => (
                        <label key={area} className="inline-flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            name={`coverage_${area}`}
                            checked={formData.serviceCoverageArea[area]}
                            onChange={handleInputChange}
                            className="form-checkbox h-5 w-5 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"
                          />
                          <span className="text-gray-700 font-medium">{area}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* 7. Legal Compliance */}
              {activeSection === 7 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { key: 'gstRegistered', label: 'GST Registration' },
                    { key: 'msmeRegistered', label: 'MSME / UDYAM Registration' },
                    { key: 'fssaiRegistered', label: 'FSSAI (Food related)' },
                    { key: 'tradeLicenseRegistered', label: 'Trade License' },
                    { key: 'iecRegistered', label: 'Import Export Code (IEC)' },
                    { key: 'isoRegistered', label: 'ISO Certification' },
                    { key: 'professionalCertificateRegistered', label: 'Trained / Professional Certificate' },
                    { key: 'otherApprovalRegistered', label: 'Any Other Relevant Approval' }
                  ].map(item => (
                    <div key={item.key} className="flex items-center justify-between p-4 border rounded-lg bg-gray-50">
                      <span className="font-medium text-gray-700">{item.label}</span>
                      <div className="space-x-4">
                        <label className="inline-flex items-center cursor-pointer">
                          <input
                            type="radio"
                            name={item.key} // IMPORTANT: this logic needs fix for checkbox vs radio if strict yes/no. 
                            // Actually user might want Yes/No toggle.
                            // Let's use Checkbox for "Yes" effectively.
                            checked={formData[item.key]}
                            onChange={(e) => setFormData(prev => ({ ...prev, [item.key]: true }))}
                            className="form-radio text-indigo-600"
                          />
                          <span className="ml-2 text-sm">Yes</span>
                        </label>
                        <label className="inline-flex items-center cursor-pointer">
                          <input
                            type="radio"
                            name={item.key}
                            checked={!formData[item.key]}
                            onChange={(e) => setFormData(prev => ({ ...prev, [item.key]: false }))}
                            className="form-radio text-gray-400"
                          />
                          <span className="ml-2 text-sm">No</span>
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* 8. Document Upload */}
              {activeSection === 8 && (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">GST / MSME / FSSAI / Other Certificate Upload *</label>
                    <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-indigo-500 transition-colors bg-gray-50">
                      <div className="space-y-1 text-center">
                        <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                          <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <div className="flex text-sm text-gray-600">
                          <label htmlFor="certificateFiles" className="relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500">
                            <span>Upload a file</span>
                            <input id="certificateFiles" name="certificateFiles" type="file" multiple className="sr-only" onChange={(e) => handleFileChange(e, 'certificateFiles', true)} accept=".jpg,.jpeg,.png,.pdf" />
                          </label>
                          <p className="pl-1">or drag and drop</p>
                        </div>
                        <p className="text-xs text-gray-500">PNG, JPG, PDF up to 10MB</p>
                      </div>
                    </div>
                    {/* File List */}
                    {formData.certificateFiles.length > 0 && (
                      <ul className="mt-4 space-y-2">
                        {formData.certificateFiles.map((file, index) => (
                          <li key={index} className="flex justify-between items-center bg-gray-100 p-2 rounded">
                            <span className="text-sm truncate">{file.name}</span>
                            <button type="button" onClick={() => removeFile('certificateFiles', index)} className="text-red-500 hover:text-red-700 text-sm">Remove</button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Trained / Qualified Certificate Upload (if applicable)</label>
                    <input
                      type="file"
                      multiple
                      onChange={(e) => handleFileChange(e, 'trainedCertificateFiles', true)}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                    />
                  </div>
                </div>
              )}

              {/* 9. Media Upload */}
              {activeSection === 9 && (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Company Logo</label>
                    <input
                      type="file"
                      onChange={(e) => handleFileChange(e, 'logo', false)}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                      accept="image/*"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Product / Service Photos (Minimum 3) *</label>
                    <input
                      type="file"
                      multiple
                      onChange={(e) => handleFileChange(e, 'productPhotos', true)}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                      accept="image/*"
                    />
                    {formData.productPhotos.length > 0 && (
                      <div className="mt-4 grid grid-cols-4 gap-4">
                        {formData.productPhotos.map((file, index) => (
                          <div key={index} className="relative group">
                            <img src={URL.createObjectURL(file)} alt="preview" className="h-24 w-full object-cover rounded-lg" />
                            <button
                              type="button"
                              onClick={() => removeFile('productPhotos', index)}
                              className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity transform translate-x-1/2 -translate-y-1/2"
                            >
                              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Office / Factory / Work Place Photos (Optional)</label>
                    <input
                      type="file"
                      multiple
                      onChange={(e) => handleFileChange(e, 'officePhotos', true)}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                      accept="image/*"
                    />
                  </div>
                </div>
              )}

              {/* 10. About Your Business */}
              {activeSection === 10 && (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">About Your Business (Brief Profile) *</label>
                    <textarea name="description" value={formData.description} onChange={handleInputChange} className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-3 border" rows="4" required></textarea>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">How Your Business Is Better Than Others (USP / Strengths) *</label>
                    <textarea name="uspStrengths" value={formData.uspStrengths} onChange={handleInputChange} className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-3 border" rows="3" required></textarea>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Awards / Achievements (if any)</label>
                    <textarea name="awardsAchievements" value={formData.awardsAchievements} onChange={handleInputChange} className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-3 border" rows="2"></textarea>
                  </div>
                </div>
              )}

              {/* 11. Review (Skipped for brevity/logic simplicitly, can just check prev steps) */}
              {/* For now, we jump to Declaration */}

              {/* 12. Declaration */}
              {(activeSection === 11 || activeSection === 12) && (
                <div className="space-y-6">
                  <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                    <div className="flex">
                      <div className="ml-3">
                        <h3 className="text-lg font-medium text-yellow-800">Declaration</h3>
                        <div className="mt-4 space-y-4 text-yellow-700">
                          <label className="flex items-start space-x-3 cursor-pointer">
                            <input
                              type="checkbox"
                              name="declarationAccepted"
                              checked={formData.declarationAccepted}
                              onChange={handleInputChange}
                              className="form-checkbox h-5 w-5 text-indigo-600 rounded mt-1"
                              required
                            />
                            <div className="text-sm">
                              <p className="font-bold mb-2">Yes, I confirm that:</p>
                              <ul className="list-disc pl-5 space-y-1">
                                <li>I belong to the Tamil community and my mother tongue is Tamil</li>
                                <li>I am the Owner / Director / Partner / Authorized Representative of the above company</li>
                                <li>I hereby affirm and assure all users and customers that our products and/or services are delivered with 100% trustworthiness, fair value for money, and quality standards, upholding the excellence and integrity of Tamil-owned products and services.</li>
                                <li>All information and documents provided are true and correct</li>
                                <li>I take full responsibility for the authenticity of the above-mentioned details</li>
                                <li>I agree to publish my business details on GoodBuyTamizha platform</li>
                              </ul>
                            </div>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Applicant Full Name *</label>
                      <input type="text" name="applicantSignature" value={formData.applicantSignature || formData.ownerName} onChange={handleInputChange} className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-3 border" required placeholder="Type your full name as digital signature" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                      <input type="date" name="declarationDate" value={formData.declarationDate} disabled className="w-full rounded-lg border-gray-300 bg-gray-100 p-3 border text-gray-500" />
                    </div>
                  </div>

                  {/* Referral Code */}
                  <div className="mt-5">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Referral Code <span className="text-gray-400 font-normal">(Optional)</span></label>
                    <input
                      type="tel"
                      name="referralCode"
                      value={formData.referralCode}
                      onChange={handleInputChange}
                      maxLength={15}
                      placeholder="Enter the mobile number of person who referred you"
                      className="w-full md:w-1/2 rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-3 border"
                    />
                    <p className="mt-1 text-xs text-gray-400">If someone referred you to GoodBuyTamizha, enter their registered mobile number here.</p>
                  </div>
                </div>
              )}

              {/* Footer Navigation */}
              <div className="mt-10 pt-5 border-t border-gray-200 flex justify-between">
                <button
                  type="button"
                  onClick={prevSection}
                  disabled={activeSection === 1 || loading}
                  className={`px-6 py-3 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 shadow-sm transition-colors ${activeSection === 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  Previous
                </button>

                {activeSection < 12 ? (
                  <button
                    type="button"
                    onClick={nextSection}
                    className="px-6 py-3 rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-700 shadow-lg shadow-indigo-200 transition-colors"
                  >
                    Next Step
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={loading || !formData.declarationAccepted}
                    className={`px-8 py-3 rounded-lg bg-green-600 text-white font-bold hover:bg-green-700 shadow-lg shadow-green-200 transition-all transform hover:scale-105 ${loading ? 'opacity-70 cursor-wait' : ''}`}
                  >
                    {loading ? 'Submitting...' : 'Submit Final Application'}
                  </button>
                )}
              </div>
            </form>
          </div>

          <p className="text-center text-gray-500 mt-8 text-sm">
            Need help? Contact <a href="#" className="text-indigo-600 hover:text-indigo-500">GoodBuyTamizha Support</a>
          </p>
        </div>
      </div>
    </>
  );
};

export default SupplierRegister;
